import SettingsSection from "../../components/SettingsSection";
import { z } from "zod";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import styles from "./Brand.module.scss";
import Radio from "../../../../ui/Radio/Radio";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import TextInput from "../../../../ui/TextInput/TextInput";
import Select from "../../../../ui/Select/Select";
import Textarea from "../../../../ui/Textarea/Textarea";
import Header from "../../components/Header/Header";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";

const BrandSettingsQuery = graphql(/* GraphQL */ `
  query brandSettings {
    brandSettings {
      companyName
      companyDescription
      useCustomTone
      toneProfile
      customTone
      audience
    }
  }
`);

const UpdateBrandSettingsMutation = graphql(/* GraphQL */ `
  mutation updateBrandSettings($input: UpdateBrandSettingsInput!) {
    updateBrandSettings(input: $input) {
      companyName
      companyDescription
      useCustomTone
      toneProfile
      customTone
      audience
    }
  }
`);

const schema = z.object({
  companyName: z.string(),
  companyDescription: z.string(),
  toneAndLanguage: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("toneProfile"),
      toneProfile: z.string(),
    }),
    z.object({
      type: z.literal("customTone"),
      customTone: z.string(),
    }),
  ]),
  audience: z.string(),
});

type BrandFormData = z.infer<typeof schema>;

function ToneAndLanguage({ control }: { control: Control<BrandFormData> }) {
  const toneAndLanguage = useWatch({ control, name: "toneAndLanguage" });

  return (
    <div className={styles.toneAndLanguage}>
      <Controller
        name="toneAndLanguage.type"
        control={control}
        render={({ field: { onChange, name, ref, value } }) => (
          <div className={styles.radios}>
            <label htmlFor="toneProfile" className={styles.radioLabel}>
              <Radio
                name={name}
                onChange={() => {
                  onChange("toneProfile");
                }}
                ref={ref}
                checked={value === "toneProfile"}
                id="toneProfile"
              />
              Tone Profile
            </label>
            {toneAndLanguage?.type === "toneProfile" && (
              <Controller
                name="toneAndLanguage.toneProfile"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    data={[
                      {
                        value: "PROFESSIONAL",
                        label: "Professional",
                      },
                      {
                        value: "HUMOROUS",
                        label: "Humorous",
                      },
                      {
                        value: "ACADEMIC",
                        label: "Academic",
                      },
                      {
                        value: "PERSUASIVE",
                        label: "Persuasive",
                      },
                      {
                        value: "CONVERSATIONAL",
                        label: "Conversational",
                      },
                      {
                        value: "TECHNICAL",
                        label: "Technical",
                      },
                    ]}
                  />
                )}
              />
            )}
            <label htmlFor="customTone" className={styles.radioLabel}>
              <Radio
                name={name}
                onChange={() => {
                  onChange("customTone");
                }}
                ref={ref}
                checked={value === "customTone"}
                id="customTone"
              />
              Custom Tone
            </label>
            {toneAndLanguage?.type === "customTone" && (
              <Controller
                name="toneAndLanguage.customTone"
                control={control}
                render={({ field }) => {
                  return <Textarea {...field} autosize maxRows={10} />;
                }}
              />
            )}
          </div>
        )}
      />
    </div>
  );
}

function BrandSettingsForm({
  defaultValues,
  onSubmit,
}: {
  defaultValues: BrandFormData;
  onSubmit: (data: BrandFormData) => Promise<void>;
}) {
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<BrandFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmitWrapper = useCallback(
    async (data: BrandFormData) => {
      await onSubmit(data);

      reset(data);

      showToast({
        title: "Brand settings saved",
        variant: "success",
      });
    },
    [onSubmit, reset, showToast],
  );

  return (
    <form onSubmit={handleSubmit(onSubmitWrapper)}>
      <UnsavedChangesModal isDirty={isDirty} />
      <Header
        title="Brand Settings"
        description="Manage how Macaw refers to your brand"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        onCancel={() => reset()}
      />
      <SettingsSection label="Company name">
        <div className={styles.inputSection}>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => <TextInput {...field} />}
          />
        </div>
      </SettingsSection>
      <SettingsSection
        label="Company description"
        description="A short overview of your company"
      >
        <div className={styles.inputSection}>
          <Controller
            name="companyDescription"
            control={control}
            render={({ field }) => (
              <Textarea {...field} autosize maxRows={10} />
            )}
          />
        </div>
      </SettingsSection>
      <SettingsSection
        label="Tone & Language"
        description="How your brand speaks"
      >
        <div className={styles.inputSection}>
          <ToneAndLanguage control={control} />
        </div>
      </SettingsSection>
      <SettingsSection
        label="Audience"
        description="A description of the core audience you are writing for"
      >
        <div className={styles.inputSection}>
          <Controller
            name="audience"
            control={control}
            render={({ field }) => (
              <Textarea {...field} autosize maxRows={10} />
            )}
          />
        </div>
      </SettingsSection>
    </form>
  );
}

function Brand() {
  const [brandSettingsResponse] = useQuery({ query: BrandSettingsQuery });
  const [, updateBrandSettings] = useMutation(UpdateBrandSettingsMutation);

  const onSubmit = useCallback(
    async (data: BrandFormData) => {
      await updateBrandSettings({
        input: {
          companyName: data.companyName,
          companyDescription: data.companyDescription,
          audience: data.audience,
          useCustomTone: data.toneAndLanguage.type === "customTone",
          toneProfile:
            data.toneAndLanguage.type === "toneProfile"
              ? data.toneAndLanguage.toneProfile
              : undefined,
          customTone:
            data.toneAndLanguage.type === "customTone"
              ? data.toneAndLanguage.customTone
              : undefined,
        },
      });
    },
    [updateBrandSettings],
  );

  const defaultValues: BrandFormData = useMemo(() => {
    if (!brandSettingsResponse.data?.brandSettings) {
      return {
        companyName: "",
        companyDescription: "",
        audience: "",
        toneAndLanguage: {
          type: "toneProfile",
          toneProfile: "PROFESSIONAL",
        },
      };
    }

    const {
      companyName,
      companyDescription,
      customTone,
      useCustomTone,
      toneProfile,
      audience,
    } = brandSettingsResponse.data.brandSettings;
    return {
      companyName: companyName ?? "",
      companyDescription: companyDescription ?? "",
      audience: audience ?? "",
      toneAndLanguage: useCustomTone
        ? { type: "customTone", customTone: customTone ?? "" }
        : {
            type: "toneProfile",
            toneProfile: toneProfile ?? "PROFESSIONAL",
          },
    };
  }, [brandSettingsResponse.data?.brandSettings]);

  if (brandSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <BrandSettingsForm defaultValues={defaultValues} onSubmit={onSubmit} />
  );
}

export default Brand;
